import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema } from "@/lib/aeo";
import { Phone, MessageCircle, Mail, CheckCircle2, Info } from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Transparent Pricing — Translation, Notary, MFA, Visa | NYC Language Institute Thailand";
const DESC =
  "Clear, published prices from /page for translation /doc Notary Public, MFA legalization 3–5 days. Ask us anything — real experts reply within minutes.";

export const Route = createFileRoute("/en/pricing")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/pricing", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/pricing", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/pricing", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/pricing", "en")},
      { property: "og:url", content: `${SITE}/en/pricing` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/pricing` },
      ...hreflangLinks("/pricing", "/en/pricing"),
    ],
    scripts: [speakableSchema(), 
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "PriceSpecification",
          priceCurrency: "THB",
          name: "NYC Legal Service Pricing",
        }),
      },
    ],
  }),
  component: EnPricingPage,
});

const PRICING = [
  {
    category: "Certified Translation",
    rows: [
      { lang: "English ⇄ Thai", price: "฿500", unit: "per A4 page" },
      { lang: "Chinese · Japanese · Korean", price: "฿700", unit: "per A4 page" },
      { lang: "European (DE/FR/ES/IT/PT)", price: "฿800", unit: "per A4 page" },
      { lang: "Arabic · Russian · Vietnamese", price: "฿900", unit: "per A4 page" },
      { lang: "Rare languages (50+)", price: "฿1,200+", unit: "per A4 page" },
    ],
  },
  {
    category: "Notary Public",
    rows: [
      { lang: "Notarial certification", price: "฿800", unit: "per document" },
      { lang: "Affidavit (signature + oath)", price: "฿1,500", unit: "per document" },
      { lang: "Power of Attorney drafting", price: "฿3,000", unit: "per document" },
      { lang: "Corporate resolution package", price: "฿5,000+", unit: "per set" },
    ],
  },
  {
    category: "MFA & Embassy Legalization",
    rows: [
      { lang: "MFA legalization (standard 3–5 days)", price: "฿800", unit: "per document" },
      { lang: "MFA express (1 business day)", price: "฿2,000", unit: "per document" },
      { lang: "Embassy attestation", price: "Varies", unit: "by embassy" },
      { lang: "Combo: Translate + MFA + Embassy", price: "From ฿2,500", unit: "per document set" },
    ],
  },
  {
    category: "Visa & Work Permit",
    rows: [
      { lang: "Initial consultation", price: "Free", unit: "30 min" },
      { lang: "Tourist · Non-O · Marriage visa", price: "฿8,000+", unit: "per case" },
      { lang: "Non-B + Work Permit (full pack)", price: "฿35,000+", unit: "per case" },
      { lang: "LTR · Elite · DTV · PR", price: "Quote", unit: "by program" },
    ],
  },
];

function EnPricingPage() {
  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Link to="/en" className="text-amber-300 hover:underline">← EN home</Link>
            <span className="text-white/30">·</span>
            <Link to="/pricing" className="text-white/80 hover:text-amber-300">ราคาภาษาไทย</Link>
          </div>
          <h1 className="mt-4 text-3xl font-extrabold sm:text-5xl">
            Transparent <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">Pricing</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Published rates. No hidden fees. Quotes confirmed in writing within 15 minutes.
          </p>
          <div className="quick-answer mt-6 max-w-2xl rounded-2xl border border-amber-400/40 bg-amber-500/10 p-5" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-amber-300">AI Quick Answer</div>
            <h2 className="text-base font-bold text-white sm:text-lg" itemProp="name">How much does certified translation cost in Bangkok?</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-white/85" itemProp="text">Certified English–Thai translation starts at THB 500 per A4 page. Asian languages (Chinese, Japanese, Korean) are THB 700, European languages THB 800, Arabic and Russian THB 900. Notary Public is THB 1,500/doc, MFA legalization 3–5 days, NAATI THB 1,300/page. All prices published — no hidden fees.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-5xl space-y-10">
          {PRICING.map((group) => (
            <div key={group.category} className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
              <div className="border-b border-white/10 bg-amber-500/10 px-6 py-3">
                <h2 className="text-base font-bold text-amber-200">{group.category}</h2>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {group.rows.map((r, i) => (
                    <tr key={r.lang} className={i % 2 === 0 ? "bg-white/[0.02]" : ""}>
                      <td className="px-6 py-3 text-white/85">{r.lang}</td>
                      <td className="px-4 py-3 text-right font-bold text-amber-300">{r.price}</td>
                      <td className="px-6 py-3 text-right text-xs text-white/80">{r.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 h-5 w-5 flex-none text-amber-300" />
              <div className="text-sm text-white/75">
                <p className="font-semibold text-white">What's always included</p>
                <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                  {["Free quote within 15 min", "PDF preview before printing", "QC by senior reviewer", "Local Bangkok delivery same-day", "DHL/FedEx tracking worldwide", "Lifetime re-issue if lost"].map((b) => (
                    <li key={b} className="flex items-start gap-1.5"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-300" />{b}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 rounded-2xl border border-amber-400/30 bg-amber-500/5 p-6">
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white hover:bg-[#05B14C]">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <LeadFormModal trigger={
              <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10">
                <Mail className="h-4 w-4" /> Custom Quote
              </button>
            } />
          </div>
        </div>
      </section>
    <RelatedHubGrid locale="en" /></main>
  );
}
